import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import html2pdf from "html2pdf.js/src/index.js";
import Backdrop from "../../../UI/Backdrop/Backdrop";

import HostInformationCard from "../../../../Pages/AnalysisReports/ThreatHunterAnalysisReports/components/InformationCards/HostInformationCard";
import OSInformationCard from "../../../../Pages/AnalysisReports/ThreatHunterAnalysisReports/components/InformationCards/OSInformationCard";
import RiskScoreGauge from "../../../../Pages/AnalysisReports/ThreatHunterAnalysisReports/components/RiskScoreGauge/RiskScoreGauge";
import ThreatSummary from "../../../../Pages/AnalysisReports/ThreatHunterAnalysisReports/components/ThreatSummary/ThreatSummary";
import SecurityRatings from "../../../../Pages/AnalysisReports/ThreatHunterAnalysisReports/components/SecurityRatings/SecurityRatings";
import ThreatCategoriesChart from "../../../../Pages/AnalysisReports/ThreatHunterAnalysisReports/components/ThreatCategoriesChart/ThreatCategoriesChart";
import TopRiskyFiles from "../../../../Pages/AnalysisReports/ThreatHunterAnalysisReports/components/TopRiskyFiles/TopRiskyFiles";
import TopRiskyProcesses from "../../../../Pages/AnalysisReports/ThreatHunterAnalysisReports/components/TopRiskyProcesses/TopRiskyProcesses";
import SecurityIssues from "../../../../Pages/AnalysisReports/ThreatHunterAnalysisReports/components/SecurityIssues/SecurityIssues";
import ThreatDetailsFlat from "../../../../Pages/AnalysisReports/ThreatHunterAnalysisReports/components/ThreatDetailsBySeverity/ThreatDetailsFlat";
import EnrichedThreatDataTable from "../../../../Pages/AnalysisReports/ThreatHunterAnalysisReports/components/EnrichedDataVisualization/EnrichedThreatDataTable";

import { RequestAgentAnalysisData } from "../../../../store/actions/integrationsActions";
import { RequestGetReportLogo } from "../../../../store/actions/analysisReportActions";
import {
  getReportTime,
  getVerdict,
} from "../../../../Pages/ThreatHunterReportExport/utils/dataSanitization";
import { THREAT_HUNTER_REPORT_LOGO } from "../../../../utils/constants/reportLogos";

import "./styles.scss";

//GET AGENT REPORT ID IN URL
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const ThreatHunterReportPDF = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const middleRef = useRef(null);
  let query = useQuery();

  const agentAnalysisData = useSelector((state) => state.integrations.agentAnalysisData);
  const reportLogo = useSelector((state) => state.analysisReports.reportLogo);

  const systemTab = query.get("s");
  const riskTab = query.get("r");
  const categoriesTab = query.get("c");
  const filesTab = query.get("f");
  const processesTab = query.get("p");
  const issuesTab = query.get("i");
  const threatDetailsTab = query.get("t");
  const enrichedTab = query.get("e");

  const [currentDate] = useState(new Date());

  useEffect(() => {
    dispatch(RequestAgentAnalysisData({ originalID: query.get("data") }));
    dispatch(RequestGetReportLogo());
  }, [dispatch]);

  const generatePdf = () => {
    const reportContainer = document.querySelector(".thunter-reportpdf-main__content");

    const unwantedDivSelectors = [];

    if (systemTab === "false") {
      unwantedDivSelectors.push(".thunter-reportpdf-main__content--system");
    }
    if (riskTab === "false") {
      unwantedDivSelectors.push(".thunter-reportpdf-main__content--risk");
    }
    if (categoriesTab === "false") {
      unwantedDivSelectors.push(".thunter-reportpdf-main__content--categories");
    }
    if (filesTab === "false") {
      unwantedDivSelectors.push(".thunter-reportpdf-main__content--files");
    }
    if (processesTab === "false") {
      unwantedDivSelectors.push(".thunter-reportpdf-main__content--processes");
    }
    if (issuesTab === "false") {
      unwantedDivSelectors.push(".thunter-reportpdf-main__content--issues");
    }
    if (threatDetailsTab === "false") {
      unwantedDivSelectors.push(".thunter-reportpdf-main__content--threat-details");
    }
    if (enrichedTab === "false") {
      unwantedDivSelectors.push(".thunter-reportpdf-main__content--enriched");
    }

    unwantedDivSelectors.forEach((selector) => {
      const unwantedDivs = reportContainer.querySelectorAll(selector);
      unwantedDivs.forEach((unwantedDiv) => {
        unwantedDiv.remove();
      });
    });

    const hostName = agentAnalysisData?.raw_report?.systeminfo?.hostname || "Machine";

    const options = {
      margin: 10,
      filename: "Report - " + hostName + ".pdf",
      image: { type: "jpeg", quality: 0.5 },
      html2canvas: { dpi: 75, scale: 2, letterRendering: true },
      jsPDF: { unit: "mm", format: "a3", orientation: "portrait" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    const pdfPromise = new Promise((resolve, reject) => {
      html2pdf(reportContainer, options)
        .then(() => resolve())
        .catch((error) => {
          console.error("Error during PDF generation:", error);
          reject(error);
        });
    });

    pdfPromise.then(() => {
      history.push(`threat-hunter-report-export?data=${query.get("data")}`);
    });
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "visible";
    };
  }, []);

  useEffect(() => {
    if (middleRef.current) {
      const middleElement = middleRef.current;
      const middleElementTop = middleElement.offsetTop;
      const middleElementHeight = middleElement.clientHeight;
      const windowHeight = window.innerHeight;

      const scrollTo = middleElementTop - (windowHeight - middleElementHeight) / 2;
      window.scrollTo(0, scrollTo);
    }

    // The charts need a moment to draw into their canvases before html2canvas
    // rasterizes them, and the effect re-runs (clearing the timer) whenever the
    // report data changes, so capture happens once the data has settled.
    const timeoutId = setTimeout(() => {
      if (agentAnalysisData && Object.keys(agentAnalysisData).length > 0) {
        generatePdf();
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [agentAnalysisData, reportLogo]);

  const systemInfo = agentAnalysisData?.raw_report?.systeminfo || {};

  return (
    <div className="thunter-reportpdf">
      <div className="thunter-reportpdf-loading">
        <Backdrop absolute={true} show={true} message="Generating PDF. Please wait..." />
      </div>
      <div className="thunter-reportpdf-main">
        <div ref={middleRef} className="thunter-reportpdf-main__content">
          <p className="thunter-reportpdf-main__content--date">
            {currentDate.toLocaleDateString("tr-TR", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })}
            -cyberfortress©
          </p>
          <img
            alt="Report logo"
            className="thunter-reportpdf-main__content--img"
            src={reportLogo ? "data:image/png;base64," + reportLogo : THREAT_HUNTER_REPORT_LOGO}
          />
          <p className="thunter-reportpdf-main__content--header">Threat Hunter Report</p>
          <p className="thunter-reportpdf-main__content--subheader">
            {systemInfo.hostname || "Unknown host"} — {getReportTime(agentAnalysisData)} —{" "}
            {getVerdict(agentAnalysisData?.activity_name, agentAnalysisData?.risk_score)}
          </p>

          <div className="thunter-reportpdf-main__content--system">
            <h2>System Information</h2>
            <div className="thunter-reportpdf-main__content--system-content">
              <HostInformationCard
                clientID={agentAnalysisData?.clientID || ""}
                hostData={systemInfo}
              />
              <OSInformationCard osData={systemInfo.osstatus || {}} />
            </div>
          </div>

          <div className="thunter-reportpdf-main__content--risk">
            <h2>Risk Overview</h2>
            <div className="thunter-reportpdf-main__content--risk-content">
              <SecurityRatings ratings={agentAnalysisData?.system_info?.security_ratings || {}} />
              <RiskScoreGauge riskScore={agentAnalysisData?.risk_score} />
              <ThreatSummary threatCounts={agentAnalysisData?.threat_counts} />
            </div>
          </div>

          <div className="thunter-reportpdf-main__content--categories">
            <h2>Threat Categories</h2>
            <div className="thunter-reportpdf-main__content--categories-content">
              <ThreatCategoriesChart
                threatCategories={agentAnalysisData?.top_threat_categories}
                report={true}
              />
            </div>
          </div>

          <div className="thunter-reportpdf-main__content--files">
            <h2>Top Risky Files</h2>
            <div className="thunter-reportpdf-main__content--files-content">
              <TopRiskyFiles files={agentAnalysisData?.top_risky_files} />
            </div>
          </div>

          <div className="thunter-reportpdf-main__content--processes">
            <h2>Top Risky Memory Processes</h2>
            <div className="thunter-reportpdf-main__content--processes-content">
              <TopRiskyProcesses processes={agentAnalysisData?.top_risky_memory_processes || []} />
            </div>
          </div>

          <div className="thunter-reportpdf-main__content--issues">
            <h2>Security Issues</h2>
            <div className="thunter-reportpdf-main__content--issues-content">
              <SecurityIssues issues={agentAnalysisData?.security_issues} />
            </div>
          </div>

          <div className="thunter-reportpdf-main__content--threat-details">
            <h2>Threat Details by Severity</h2>
            <div className="thunter-reportpdf-main__content--threat-details-content">
              <ThreatDetailsFlat data={agentAnalysisData?.threat_details_by_severity || {}} />
            </div>
          </div>

          <div className="thunter-reportpdf-main__content--enriched">
            <h2>Enriched Threat Data</h2>
            <div className="thunter-reportpdf-main__content--enriched-content">
              <EnrichedThreatDataTable
                analysisDetails={agentAnalysisData?.analysis_details}
                enrichedMaliciousHashes={agentAnalysisData?.enriched_malicious_hashes}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThreatHunterReportPDF;
